package fyi.moko.hotpl.ui.placeform

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.File
import kotlin.js.Promise

/**
 * JS/web actual of [rememberMultiImagePicker]: an `<input type="file">` with
 * `multiple`, capped at [MAX_PHOTOS_PER_PICK]. Each file is downscaled on a `<canvas>`
 * so its longest edge is at most [MAX_IMAGE_DIMENSION] and re-encoded as JPEG at
 * [JPEG_QUALITY] — matching Android/iOS so a phone-camera-sized photo doesn't blow past
 * `localStorage`'s ~5-10MB-per-origin budget once `JsPlaceImageStore` persists it.
 * Decoding tries [window.createImageBitmap] first, then falls back to an `<img>`
 * element (see [processPickedFile]): iOS Safari's photo library commonly hands back
 * HEIC files, and WebKit's `createImageBitmap` has historically been the less reliable
 * of the two decode paths for that format.
 *
 * The browser cannot be made to enforce the cap, so anything past it is dropped here.
 * Files are processed one at a time rather than in parallel: each one materialises a
 * full-size bitmap on a canvas, and doing six at once is how a phone browser tab gets
 * killed for memory.
 */
@Composable
actual fun rememberMultiImagePicker(): MultiImagePicker {
    val scope = rememberCoroutineScope()
    return MultiImagePicker { onResult ->
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = "image/*"
        input.multiple = true
        input.onchange = {
            val files = input.files
            val picked = buildList {
                val count = files?.length ?: 0
                for (i in 0 until minOf(count, MAX_PHOTOS_PER_PICK)) {
                    files?.item(i)?.let { add(it) }
                }
            }
            if (picked.isEmpty()) {
                onResult(emptyList())
            } else {
                scope.launch { onResult(picked.map { PickedImage(processPickedFile(it)) }) }
            }
            Unit
        }
        input.click()
    }
}

/** Shared with the camera actual — a captured file goes through the same downscale. */
internal suspend fun processPickedFile(file: File): ByteArray {
    val viaBitmap = runCatching { downscaleViaImageBitmap(file) }.getOrNull()
    if (viaBitmap != null) return viaBitmap

    val viaImgElement = runCatching { downscaleViaImgElement(file) }.getOrNull()
    if (viaImgElement != null) return viaImgElement

    console.error("Photo downscale failed via both createImageBitmap and <img>; storing original bytes")
    return readFileBytes(file)
}

private suspend fun downscaleViaImageBitmap(file: File): ByteArray {
    val bitmap = window.createImageBitmap(file).await()
    val bytes = drawToJpeg(bitmap, bitmap.width, bitmap.height) { canvasCtx, w, h ->
        canvasCtx.drawImage(bitmap, 0.0, 0.0, w.toDouble(), h.toDouble())
    }
    bitmap.close()
    return bytes
}

private suspend fun downscaleViaImgElement(file: File): ByteArray {
    val url = URL.Companion.createObjectURL(file)
    try {
        val img = document.createElement("img") as HTMLImageElement
        Promise<Unit> { resolve, reject ->
            img.onload = { resolve(Unit) }
            img.onerror = { _, _, _, _, _ -> reject(RuntimeException("<img> failed to decode file")) }
            img.src = url
        }.await()
        return drawToJpeg(img, img.naturalWidth, img.naturalHeight) { canvasCtx, w, h ->
            canvasCtx.drawImage(img, 0.0, 0.0, w.toDouble(), h.toDouble())
        }
    } finally {
        URL.Companion.revokeObjectURL(url)
    }
}

private suspend fun <T> drawToJpeg(
    source: T,
    sourceWidth: Int,
    sourceHeight: Int,
    draw: (CanvasRenderingContext2D, Int, Int) -> Unit,
): ByteArray {
    val longest = maxOf(sourceWidth, sourceHeight)
    val ratio = if (longest > MAX_IMAGE_DIMENSION) MAX_IMAGE_DIMENSION.toDouble() / longest else 1.0
    val targetW = (sourceWidth * ratio).toInt().coerceAtLeast(1)
    val targetH = (sourceHeight * ratio).toInt().coerceAtLeast(1)

    val canvas = document.createElement("canvas") as HTMLCanvasElement
    canvas.width = targetW
    canvas.height = targetH
    val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
    draw(ctx, targetW, targetH)

    val blob: Blob = Promise<Blob> { resolve, _ ->
        canvas.asDynamic().toBlob({ b: Blob -> resolve(b) }, "image/jpeg", JPEG_QUALITY / 100.0)
    }.await()
    return blobBytes(blob)
}

private suspend fun readFileBytes(file: File): ByteArray = blobBytes(file)

private suspend fun blobBytes(blob: Blob): ByteArray {
    val buffer: ArrayBuffer = blob.asDynamic().arrayBuffer().unsafeCast<Promise<ArrayBuffer>>().await()
    val uint8 = Uint8Array(buffer)
    return ByteArray(uint8.length) { i -> uint8.asDynamic()[i] as Byte }
}
